import { match } from 'ts-pattern'
import { Severity } from '../../utils/api-client/api-schemas.ts'
import { formatSeverityLabel } from '../../utils/scale-utils.ts'
import * as styles from './severity-badge.css.ts'

type SeverityBadgeProps = {
  severity: Severity | null | undefined
  isAnalyzed?: boolean
}

export const SeverityBadge: React.FC<SeverityBadgeProps> = ({ severity, isAnalyzed = true }) => {
  const text = match(severity)
    .with(null, () => 'Unknown')
    .otherwise(() => severity?.label ?? 'Unknown')

  // A standard label names its level outright, whatever scale the tool ranks on (0–10 and 0–100 both occur).
  // Anything else (Blocker, Major…) falls back to six 20-wide rank bands.
  const color = getStandardLabelBand(severity?.label) ?? getRankBand(severity?.rank)

  const label = capitalize(formatSeverityLabel(text))

  return (
    <span
      className={styles.badge[color]}
      data-severity={color}
      title={label}
      style={isAnalyzed === false ? { opacity: 0.5 } : undefined}
    >
      {label}
    </span>
  )
}

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)

type SeverityBand = 'unknown' | 'info' | 'low' | 'medium' | 'high' | 'critical'

const STANDARD_LABEL_BANDS = new Map<string, SeverityBand>([
  ['critical', 'critical'],
  ['high', 'high'],
  ['medium', 'medium'],
  ['low', 'low'],
  ['info', 'info'],
  ['informational', 'info'],
  ['unknown', 'unknown'],
])

const getStandardLabelBand = (label: string | undefined): SeverityBand | undefined =>
  label === undefined ? undefined : STANDARD_LABEL_BANDS.get(label.trim().toLowerCase())

const getRankBand = (rank: number | undefined): SeverityBand =>
  match(rank)
    .with(undefined, () => 'unknown' as const)
    .with(0, () => 'unknown' as const)
    .when(
      n => n > 0 && n <= 20,
      () => 'info' as const
    )
    .when(
      n => n > 20 && n <= 40,
      () => 'low' as const
    )
    .when(
      n => n > 40 && n <= 60,
      () => 'medium' as const
    )
    .when(
      n => n > 60 && n <= 80,
      () => 'high' as const
    )
    .when(
      n => n > 80,
      () => 'critical' as const
    )
    .otherwise(() => 'unknown' as const)
